import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import PageHeader from '../../components/PageHeader.jsx';
import StatCard from '../../components/StatCard.jsx';
import { ErrorBox } from '../../components/Loader.jsx';
import { SuperAdminAPI } from '../../lib/api';
import { SUPERADMIN_RESOURCES, SUPERADMIN_RESOURCE_ORDER } from './resources';

/**
 * Super Admin landing page.
 *
 * Shows headline counters (organisations / users / roles / permissions)
 * plus a tile per resource — same UX pattern as `BusinessHome` but with
 * counts up top because ops folks always look at those first.
 */
export default function SuperAdminHome() {
  const [counts, setCounts] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      SuperAdminAPI.organisations.list({ page_size: 1 }).catch(() => null),
      SuperAdminAPI.users.list({ page_size: 1 }).catch(() => null),
      SuperAdminAPI.roles.list({ page_size: 1 }).catch(() => null),
      SuperAdminAPI.permissions.list({ page_size: 1 }).catch(() => null),
    ]).then(([orgs, users, roles, perms]) => {
      if (cancelled) return;
      // DRF paginated responses include `count`; non-paginated arrays
      // don't — we fall back to length in that case.
      const get = (d) => d && (d.count ?? (Array.isArray(d) ? d.length : 0));
      setCounts({
        organisations: get(orgs),
        users:         get(users),
        roles:         get(roles),
        permissions:   get(perms),
      });
    }).catch((e) => !cancelled && setError(e));
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      <PageHeader
        title="🛡️ Super Admin"
        subtitle="Manage organisations, users and the global access-control catalogue."
      />
      <ErrorBox error={error} />

      {/* Counter-row colours follow the original Taskose palette
          (--primary / --success / --info / --warning).

          Each counter links to the resource list it counts, so clicking
          "Users: 412" opens those users.  The tile row below links to the
          same screens, but ops folks read the numbers first and expect
          the number itself to be the way in. */}
      <div className="ty-stats">
        <StatCard label="Organisations" value={counts.organisations} accent="#7980ff"
                  to="/super-admin/organisations" />
        <StatCard label="Users"         value={counts.users}         accent="#5fd788"
                  to="/super-admin/users" />
        <StatCard label="Roles"         value={counts.roles}         accent="#34c8de"
                  to="/super-admin/roles" />
        <StatCard label="Permissions"   value={counts.permissions}   accent="#fcdb66"
                  to="/super-admin/permissions" />
      </div>

      <div className="ty-stats" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        {SUPERADMIN_RESOURCE_ORDER.map((slug) => {
          const r = SUPERADMIN_RESOURCES[slug];
          if (!r) return null;
          return (
            <Link
              key={slug}
              to={`/super-admin/${slug}`}
              className="ty-stat"
              style={{ textDecoration: 'none', borderLeftColor: '#7980ff' }}
            >
              <div className="ty-stat__label">{r.icon} {r.label}</div>
              <div className="ty-stat__value" style={{ fontSize: 18, color: '#31394f' }}>
                Manage →
              </div>
              <div className="ty-stat__weekly">{r.singular} CRUD</div>
            </Link>
          );
        })}
        <Link
          to="/super-admin/module-defaults"
          className="ty-stat"
          style={{ textDecoration: 'none', borderLeftColor: '#7980ff' }}
        >
          <div className="ty-stat__label">🧩 Default modules</div>
          <div className="ty-stat__value" style={{ fontSize: 18, color: '#31394f' }}>
            Configure →
          </div>
          <div className="ty-stat__weekly">Activated for new organisations</div>
        </Link>
        <Link
          to="/business"
          className="ty-stat"
          style={{ textDecoration: 'none', borderLeftColor: '#5fd788' }}
        >
          <div className="ty-stat__label">🏢 Public organisation</div>
          <div className="ty-stat__value" style={{ fontSize: 18, color: '#31394f' }}>
            Business dashboard →
          </div>
          <div className="ty-stat__weekly">Users and structure on this host</div>
        </Link>
        <Link
          to="/super-admin/db-viewer"
          className="ty-stat"
          style={{ textDecoration: 'none', borderLeftColor: '#1565c0' }}
        >
          <div className="ty-stat__label">🗄️ Database Viewer</div>
          <div className="ty-stat__value" style={{ fontSize: 18, color: '#31394f' }}>
            Browse →
          </div>
          <div className="ty-stat__weekly">Tables, rows, inline edit</div>
        </Link>
        <Link
          to="/super-admin/login-security"
          className="ty-stat"
          style={{ textDecoration: 'none', borderLeftColor: '#f05a5a' }}
        >
          <div className="ty-stat__label">🛡️ Bot Security</div>
          <div className="ty-stat__value" style={{ fontSize: 18, color: '#31394f' }}>
            Configure →
          </div>
          <div className="ty-stat__weekly">Login security policy</div>
        </Link>
      </div>
    </>
  );
}
